import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import katex from 'katex'
import 'katex/dist/katex.min.css'
import {
  ArrowLeft,
  Check,
  CheckCircle2,
  Clock,
  Hourglass,
  Loader2,
  LogOut,
  RotateCcw,
  Sparkles,
  Trophy,
  WifiOff,
  X,
  XCircle,
} from 'lucide-react'
import type { LeaderboardEntry, QuestionStartedPayload } from '@arabquiz/api-client'
import { useGameState } from './GameStateProvider'
import { arabicOnlyExplanation } from '../utils/arabicOnlyExplanation'

/**
 * Shared player-side in-game UI. Renders the lobby / question / results /
 * final phases for a player based on `GameStateProvider`'s current state.
 *
 * Both player entry points use this surface:
 *   - `/play` (the public, PWA-cached PIN entry route) renders it directly
 *     after a successful `JoinAsPlayer`. There's no navigation in that flow
 *     — the same `/play` page is the player's experience for the entire
 *     game, which keeps the SignalR connection alive and dodges the gameId
 *     bootstrap problem (PlayerJoinedPayload carries no gameId).
 *   - `/games/play/$gameId` (an authenticated, URL-addressable entry) uses
 *     it for resume / spectate flows where the gameId is known up front.
 *
 * Visual direction: phone-first, projector-bright. Single dominant action
 * per screen, oversized 1/2/3/4 tiles for MCQ (matched colours from the
 * host's reveal so players "see" their pick from across the room), and a
 * loud, animated state for the moment after an answer is locked in.
 *
 * Keyboard shortcuts (1–4 for MCQ, 1–2 for True/False) double as desktop
 * affordances when this is opened on a laptop; mobile users tap.
 *
 * Solo mode (optional props, all defaulting off): when the authenticated host
 * plays their own game on one device, the host route renders this same surface
 * with `solo` set. The deltas are small and gated entirely on `solo`: the
 * results screen hides the "waiting" line and shows an advance button, the
 * final screen uses solo copy, a top-corner exit control appears, and the
 * "waiting for host" overlay is suppressed (the solo player IS the host). The
 * public `/play` and `/games/play/$gameId` callers pass no props, so solo
 * defaults false and their behaviour is unchanged.
 */
type PlayerGameViewProps = {
  solo?: boolean | undefined
  onAdvance?: (() => void) | undefined
  onExit?: (() => void) | undefined
  advanceLabel?: string | undefined
  exitLabel?: string | undefined
}

export function PlayerGameView({
  solo = false,
  onAdvance,
  onExit,
  advanceLabel,
  exitLabel,
}: PlayerGameViewProps = {}) {
  const { t } = useTranslation('game')
  const { state, invoke, dispatch } = useGameState()

  // The server's `SubmitAnswer` hub method takes a "gameId" string, but the
  // player only ever sees a PIN. The hub treats either form interchangeably
  // (it resolves the active game from the player's connection group), so we
  // hand it `state.gameId` first (set when the player came in via the
  // /games/play/$gameId route) and fall back to `state.pin` for the
  // anonymous /play flow.
  const gameRef = state.gameId ?? state.pin ?? ''

  // Local "we've already submitted for this question" flag — re-anchored on
  // every new question so the submitted UI clears as soon as the next round
  // begins. The reducer doesn't track this because submission is a purely
  // client-side gesture (the server's authoritative ack arrives as the
  // ANSWER_RECEIVED dispatch, which mutates score, not phase).
  const [submittedAnswer, setSubmittedAnswer] = useState<string | null>(null)
  // Order/Sequence work-in-progress selection. Each entry is an index into
  // `currentQuestion.items` (the displayed, shuffled list). Tapping an item
  // appends it; tapping a slot below removes it. Submission stringifies
  // the corresponding original indices from `itemOrder` (server-truth).
  const [orderSelection, setOrderSelection] = useState<number[]>([])

  const currentQuestionIndex = state.currentQuestion?.questionIndex ?? null
  // Per-question result is server-authoritative (state.lastResult, set by the
  // `answerResult` event on reveal). We only surface it when it matches the
  // current question, so a stale result from a previous round never leaks in.
  const lastResult =
    state.lastResult && state.lastResult.questionIndex === currentQuestionIndex
      ? { points: state.lastResult.points, isCorrect: state.lastResult.isCorrect }
      : null
  // A question is "answered" if either our optimistic local flag is set OR the
  // SERVER says this exact question index was already answered. The latter is
  // what makes refresh-during-a-question safe: `submittedAnswer` (local React
  // state) is wiped on reload, but on reconnect the hub re-sends the question
  // and an `alreadyAnswered` event, which lands in `state.answeredQuestionIndex`
  // — so the restored UI shows "submitted" instead of inviting a double-answer.
  const serverAnswered =
    currentQuestionIndex !== null && state.answeredQuestionIndex === currentQuestionIndex
  const hasSubmitted = submittedAnswer !== null || serverAnswered
  useEffect(() => {
    // Re-anchor per-question UI state when a new question starts. We key on
    // the question index so a quiz that happens to repeat seconds/options
    // across rounds still clears the "submitted" badge. (The per-question
    // result lives in shared state now and is reset by QUESTION_STARTED.)
    if (state.phase === 'question') {
      setSubmittedAnswer(null)
      setOrderSelection([])
    }
  }, [state.phase, currentQuestionIndex])

  // Keyboard shortcuts: 1-4 for MCQ, 1-2 for TF. Only active during the
  // question phase and only when the player hasn't already submitted.
  useEffect(() => {
    if (state.phase !== 'question' || !state.currentQuestion || hasSubmitted) {
      return
    }
    const handler = (e: KeyboardEvent) => {
      // Ignore when the user is typing in any input (defence-in-depth even
      // though there are no inputs on the in-game player screen today —
      // future chat / nickname-edit features would otherwise hijack 1-4).
      const target = e.target as HTMLElement | null
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) {
        return
      }
      const q = state.currentQuestion
      if (!q) return
      if (q.type === 'MultipleChoice') {
        const idx = Number.parseInt(e.key, 10)
        if (Number.isInteger(idx) && idx >= 1 && idx <= 4) {
          const options = q.options ?? []
          const optionIndex = idx - 1
          if (optionIndex < options.length) {
            void submitAnswer(String(optionIndex))
          }
        }
      } else if (q.type === 'TrueFalse') {
        if (e.key === '1') {
          void submitAnswer('true')
        } else if (e.key === '2') {
          void submitAnswer('false')
        }
      } else if (q.type === 'OrderSequence') {
        // Keyboard shortcuts pick the next un-picked displayed item by its
        // 1-based position. Useful on desktop / projector setups; phones use
        // the tappable tiles below.
        const items = q.items ?? []
        const idx = Number.parseInt(e.key, 10)
        if (Number.isInteger(idx) && idx >= 1 && idx <= items.length) {
          const itemIndex = idx - 1
          setOrderSelection((prev) =>
            prev.includes(itemIndex) ? prev : [...prev, itemIndex],
          )
        }
      }
    }
    window.addEventListener('keydown', handler)
    return () => {
      window.removeEventListener('keydown', handler)
    }
    // `submitAnswer` is recreated each render but its identity is irrelevant
    // here — the keydown handler captures the latest closure. We
    // intentionally limit deps to the phase / question / submitted-state
    // tuple to avoid re-binding the listener on every score tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase, state.currentQuestion, submittedAnswer])

  const submitAnswer = async (answerValue: string) => {
    if (hasSubmitted) return
    if (!state.playerToken) return
    // Optimistically lock the UI before the round-trip — the hub is
    // authoritative for scoring, but waiting for that ack to render the
    // "submitted" state would let an impatient player double-tap and
    // accidentally fire a second submit.
    setSubmittedAnswer(answerValue)
    // Mark this question answered in shared state too, so the "submitted"
    // status is keyed to the question index and survives a refresh-driven
    // re-render off `state.answeredQuestionIndex`.
    if (currentQuestionIndex !== null) {
      dispatch({ type: 'ANSWER_SUBMITTED', questionIndex: currentQuestionIndex })
    }
    try {
      await invoke('SubmitAnswer', gameRef, answerValue, state.playerToken)
    } catch {
      // Roll back so the player can retry — the reducer already surfaces
      // a sticky error via the 'gameLost' / 'error' channels if the hub
      // ends the game; a transient submit failure shouldn't strand them.
      setSubmittedAnswer(null)
    }
  }

  if (state.phase === 'error') {
    // The hub reports failures as stable English codes (GameNotFound,
    // NicknameTaken, …); map them to localized copy here so players see
    // Arabic, and fall back to a generic message for anything unrecognised
    // (e.g. a free-text `gameLost` reason or a transport error string).
    const errorMessages: Record<string, string> = {
      GameNotFound: t('errors.GameNotFound'),
      InvalidNickname: t('errors.InvalidNickname'),
      NicknameTaken: t('errors.NicknameTaken'),
      RoomFull: t('errors.RoomFull'),
      NoQuestions: t('errors.NoQuestions'),
      InvalidState: t('errors.InvalidState'),
      InvalidToken: t('errors.InvalidToken'),
      PlayerNotFound: t('errors.PlayerNotFound'),
      Unauthorized: t('errors.Unauthorized'),
      SessionExpired: t('errors.SessionExpired'),
      HostAbandoned: t('errors.HostAbandoned'),
    }
    const message = (state.error && errorMessages[state.error]) || t('errors.unknown')
    return (
      <PlayerShell solo={solo} onExit={onExit} exitLabel={exitLabel}>
        <ErrorPanel
          message={message}
          retryLabel={t('backToJoin')}
          // Drop back to a clean slate so `<PlayInner />` re-renders the
          // join form — letting the player re-enter the PIN instead of
          // being stranded on the error screen (e.g. after a wrong PIN).
          onRetry={() => dispatch({ type: 'RESET' })}
        />
      </PlayerShell>
    )
  }

  if (state.phase === 'lobby') {
    return (
      <PlayerShell solo={solo} onExit={onExit} exitLabel={exitLabel}>
        <LobbyPanel
          nickname={state.nickname ?? ''}
          playerCount={state.playerCount}
          headingTemplate={t('lobbyHeading', { nickname: state.nickname ?? '' })}
          waitingLabel={t('waiting')}
          hint={t('lobbyHint')}
          playersLabel={t('playersLabel')}
        />
      </PlayerShell>
    )
  }

  if (state.phase === 'question' && state.currentQuestion) {
    return (
      <PlayerShell solo={solo} onExit={onExit} exitLabel={exitLabel}>
        <QuestionPanel
          question={state.currentQuestion}
          submittedAnswer={submittedAnswer}
          isSubmitted={hasSubmitted}
          onAnswer={submitAnswer}
          orderSelection={orderSelection}
          onOrderSelect={(itemIndex) =>
            setOrderSelection((prev) =>
              prev.includes(itemIndex) ? prev : [...prev, itemIndex],
            )
          }
          onOrderClear={() => setOrderSelection([])}
          onOrderSubmit={() => {
            const q = state.currentQuestion
            if (!q || q.type !== 'OrderSequence') return
            const itemOrder = q.itemOrder ?? []
            const items = q.items ?? []
            if (orderSelection.length !== items.length) return
            // Map each displayed-item index → its canonical (correct) index,
            // then ship the comma-separated string the hub expects.
            const canonical = orderSelection
              .map((displayedIndex) => itemOrder[displayedIndex])
              .filter((n): n is number => typeof n === 'number')
            if (canonical.length !== items.length) return
            void submitAnswer(canonical.join(','))
          }}
          progressLabel={t('questionProgress', {
            current: state.currentQuestion.questionIndex + 1,
            total: state.currentQuestion.total,
          })}
          timeLabel={t('timeLeft')}
          secondsAbbr={t('secondsAbbr')}
          tapHint={t('tapToAnswer')}
          keyboardHintMcq={t('keyboardHint')}
          keyboardHintTf={t('keyboardHintTrueFalse')}
          orderTapHint={t('orderTapHint')}
          orderClearLabel={t('orderClear')}
          orderSubmitLabel={t('orderSubmit')}
          orderSlotLabel={(n: number) => t('orderSlotLabel', { n })}
          submittedLabel={t('submitted')}
          submittedHint={t('submittedHint')}
          trueLabel={t('trueLabel')}
          falseLabel={t('falseLabel')}
        />
      </PlayerShell>
    )
  }

  if (state.phase === 'results') {
    return (
      <PlayerShell solo={solo} onExit={onExit} exitLabel={exitLabel}>
        <ResultsPanel
          result={lastResult}
          totalScore={state.score}
          correctLabel={t('correctFeedback')}
          wrongLabel={t('wrongFeedback')}
          pointsLabel={(points: number) => t('pointsEarned', { points })}
          noPointsLabel={t('noPointsThisRound')}
          totalScoreLabel={t('totalScore')}
          waitingLabel={t('waiting')}
          explanation={arabicOnlyExplanation(state.explanation ?? undefined)}
          explanationLabel={t('explanation')}
          solo={solo}
          onAdvance={onAdvance}
          advanceLabel={advanceLabel}
        />
      </PlayerShell>
    )
  }

  if (state.phase === 'leaderboard') {
    return (
      <PlayerShell solo={solo} onExit={onExit} exitLabel={exitLabel}>
        <BetweenRoundsPanel
          score={state.score}
          waitingLabel={t('waiting')}
          totalScoreLabel={t('totalScore')}
        />
      </PlayerShell>
    )
  }

  if (state.phase === 'final') {
    return (
      <PlayerShell solo={solo} onExit={onExit} exitLabel={exitLabel}>
        <FinalPanel
          nickname={state.nickname ?? ''}
          score={state.score}
          leaderboard={state.leaderboard}
          heading={solo ? t('soloResultsHeading') : t('gameEnded')}
          subheading={solo ? t('soloResultsSubheading') : t('finalResults')}
          totalScoreLabel={t('totalScore')}
          rankLabel={t('yourRank')}
          rankSuffixTemplate={(total: number) => t('rankSuffix', { total })}
          solo={solo}
          onExit={onExit}
          exitLabel={exitLabel}
        />
      </PlayerShell>
    )
  }

  return (
    <PlayerShell solo={solo} onExit={onExit} exitLabel={exitLabel}>
      <ConnectingPanel message={t('connecting')} />
    </PlayerShell>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Shell
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Visual chrome shared across every phase. Phone-first viewport sizing,
 * a deep brand gradient backdrop with two off-canvas blurred orbs so the
 * background never reads as flat, and a centred column that bounds line
 * length on tablets / laptops without forcing horizontal scroll on small
 * phones.
 */
function PlayerShell({
  children,
  solo = false,
  onExit,
  exitLabel,
}: {
  children: React.ReactNode
  solo?: boolean | undefined
  onExit?: (() => void) | undefined
  exitLabel?: string | undefined
}) {
  return (
    <main className="relative min-h-screen overflow-hidden bg-gradient-to-bl from-brand-900 via-brand-800 to-brand-900 text-white">
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.18),transparent_60%)]"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute -top-24 -end-24 h-72 w-72 rounded-full bg-brand-400/20 blur-3xl"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute -bottom-24 -start-24 h-72 w-72 rounded-full bg-brand-300/10 blur-3xl"
        aria-hidden="true"
      />
      {/* Solo-only exit control. PlayerShell has no leave button for normal
          players (they just close the tab); the solo host needs one so the
          game ends server-side rather than lingering until the sweeper. */}
      {solo && onExit ? (
        <button
          type="button"
          onClick={onExit}
          className="absolute end-4 top-4 sm:end-6 sm:top-6 z-40 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 backdrop-blur-sm ps-4 pe-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
        >
          <LogOut className="h-4 w-4" aria-hidden="true" />
          {exitLabel ? <span>{exitLabel}</span> : null}
        </button>
      ) : null}
      <div className="relative mx-auto flex min-h-screen w-full max-w-2xl flex-col ps-4 pe-4 sm:ps-6 sm:pe-6 py-6 sm:py-10">
        {children}
      </div>
      <HostWaitingOverlay solo={solo} />
    </main>
  )
}

/**
 * Non-dismissable overlay shown to players while the host's connection is
 * dropped (the `HostDisconnectGraceMinutes` grace window). Reads game state
 * directly so it can be mounted once inside `PlayerShell` and cover every
 * phase uniformly. Renders nothing while the host is present, and is gated off
 * on terminal phases so the existing error / final screens take over.
 */
function HostWaitingOverlay({ solo = false }: { solo?: boolean | undefined }) {
  const { t } = useTranslation('game')
  const { state } = useGameState()
  // In solo the player IS the host, so a "waiting for host" overlay is
  // nonsensical — suppress it entirely.
  if (solo) return null
  if (state.hostConnected) return null
  if (state.phase === 'error' || state.phase === 'final') return null
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-brand-900/80 backdrop-blur-sm ps-6 pe-6"
      role="status"
      aria-live="polite"
    >
      <section className="flex flex-col items-center text-center max-w-sm">
        <div className="inline-flex h-20 w-20 items-center justify-center rounded-full bg-white/10 ring-1 ring-white/15">
          <WifiOff className="h-9 w-9 text-amber-200 animate-pulse" aria-hidden="true" />
        </div>
        <h2 className="mt-6 font-display text-2xl sm:text-3xl font-bold text-white">
          {t('hostDisconnectedTitle')}
        </h2>
        <p className="mt-3 text-base text-brand-100/80">{t('hostDisconnectedBody')}</p>
        <p className="mt-2 text-sm text-brand-100/60 leading-relaxed">
          {t('hostDisconnectedHint')}
        </p>
      </section>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Phase: Lobby
// ─────────────────────────────────────────────────────────────────────────────

type LobbyPanelProps = {
  nickname: string
  playerCount: number
  headingTemplate: string
  waitingLabel: string
  hint: string
  playersLabel: string
}

function LobbyPanel({
  nickname,
  playerCount,
  headingTemplate,
  waitingLabel,
  hint,
  playersLabel,
}: LobbyPanelProps) {
  return (
    <section className="flex flex-1 flex-col items-center justify-center text-center">
      <div
        className="inline-flex h-20 w-20 items-center justify-center rounded-full bg-white/10 backdrop-blur-sm ring-1 ring-white/15"
        aria-hidden="true"
      >
        <Hourglass className="h-9 w-9 text-brand-100 animate-pulse" />
      </div>

      <h1 className="mt-6 font-display text-3xl sm:text-4xl font-bold tracking-tight text-white">
        {headingTemplate}
      </h1>

      <p
        className="mt-3 text-base sm:text-lg text-brand-100/80"
        aria-live="polite"
      >
        {waitingLabel}
      </p>
      <p className="mt-2 text-sm text-brand-100/60 max-w-xs leading-relaxed">{hint}</p>

      <div className="mt-10 inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/5 backdrop-blur-sm ps-5 pe-5 py-2.5">
        <span
          className="inline-flex h-2 w-2 rounded-full bg-emerald-400 animate-pulse"
          aria-hidden="true"
        />
        <span className="text-sm font-semibold text-brand-100/80">{playersLabel}</span>
        <span
          className="font-display text-xl font-bold tabular-nums text-white"
          aria-live="polite"
          aria-atomic="true"
        >
          {playerCount}
        </span>
      </div>

      {nickname ? (
        <span className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 ps-4 pe-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-brand-100">
          <Check className="h-3.5 w-3.5" aria-hidden="true" />
          <span>{nickname}</span>
        </span>
      ) : null}
    </section>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Phase: Question
// ─────────────────────────────────────────────────────────────────────────────

type QuestionPanelProps = {
  question: QuestionStartedPayload
  submittedAnswer: string | null
  isSubmitted: boolean
  onAnswer: (value: string) => void
  orderSelection: number[]
  onOrderSelect: (itemIndex: number) => void
  onOrderClear: () => void
  onOrderSubmit: () => void
  progressLabel: string
  timeLabel: string
  secondsAbbr: string
  tapHint: string
  keyboardHintMcq: string
  keyboardHintTf: string
  orderTapHint: string
  orderClearLabel: string
  orderSubmitLabel: string
  orderSlotLabel: (n: number) => string
  submittedLabel: string
  submittedHint: string
  trueLabel: string
  falseLabel: string
}

function QuestionPanel({
  question,
  submittedAnswer,
  isSubmitted,
  onAnswer,
  orderSelection,
  onOrderSelect,
  onOrderClear,
  onOrderSubmit,
  progressLabel,
  timeLabel,
  secondsAbbr,
  tapHint,
  keyboardHintMcq,
  keyboardHintTf,
  orderTapHint,
  orderClearLabel,
  orderSubmitLabel,
  orderSlotLabel,
  submittedLabel,
  submittedHint,
  trueLabel,
  falseLabel,
}: QuestionPanelProps) {
  const timeLeft = useCountdown(question.questionIndex, question.timeSeconds)
  const questionHtml = useMemo(() => renderMathPreview(question.text), [question.text])
  const isMcq = question.type === 'MultipleChoice'
  const isOrder = question.type === 'OrderSequence'

  return (
    <>
      <header className="flex items-center justify-between gap-3">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 ps-3 pe-3 py-1 text-xs font-semibold uppercase tracking-wider text-brand-100/80">
          {progressLabel}
        </span>
        <CompactTimer
          remaining={timeLeft}
          total={question.timeSeconds}
          label={timeLabel}
          unit={secondsAbbr}
        />
      </header>

      <article className="mt-5 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm shadow-2xl ps-5 pe-5 sm:ps-7 sm:pe-7 py-6 sm:py-7">
        <div
          className="font-display text-xl sm:text-2xl font-bold leading-relaxed text-white text-center"
          // KaTeX returns balanced, sanitised HTML (trust:false, strict:true);
          // plain text outside `$…$` delimiters is HTML-escaped before
          // joining, so injecting via dangerouslySetInnerHTML is safe.
          dangerouslySetInnerHTML={{ __html: questionHtml }}
        />
      </article>

      <div className="mt-5 text-center">
        {isSubmitted ? null : (
          <p className="text-sm text-brand-100/70">
            <span className="block sm:inline">
              {isOrder ? orderTapHint : tapHint}
            </span>
            {!isOrder ? (
              <span
                className="ms-2 hidden sm:inline text-brand-100/50"
                aria-hidden="true"
              >
                {isMcq ? keyboardHintMcq : keyboardHintTf}
              </span>
            ) : null}
          </p>
        )}
      </div>

      <section className="mt-5 flex flex-1 flex-col">
        {isSubmitted ? (
          <SubmittedBadge label={submittedLabel} hint={submittedHint} />
        ) : null}

        {isMcq ? (
          <PlayerMcqGrid
            options={question.options ?? []}
            disabled={isSubmitted}
            submittedAnswer={submittedAnswer}
            onSelect={onAnswer}
          />
        ) : isOrder ? (
          <PlayerOrderGrid
            items={question.items ?? []}
            selection={orderSelection}
            disabled={isSubmitted}
            onSelect={onOrderSelect}
            onClear={onOrderClear}
            onSubmit={onOrderSubmit}
            clearLabel={orderClearLabel}
            submitLabel={orderSubmitLabel}
            slotLabel={orderSlotLabel}
          />
        ) : (
          <PlayerTrueFalseGrid
            trueLabel={trueLabel}
            falseLabel={falseLabel}
            disabled={isSubmitted}
            submittedAnswer={submittedAnswer}
            onSelect={onAnswer}
          />
        )}
      </section>
    </>
  )
}

type CompactTimerProps = {
  remaining: number
  total: number
  label: string
  unit: string
}

function CompactTimer({ remaining, total, label, unit }: CompactTimerProps) {
  const pct = total > 0 ? Math.max(0, Math.min(100, (remaining / total) * 100)) : 0
  const isUrgent = remaining <= 5
  const isCaution = remaining <= 10 && !isUrgent
  const numeralColor = isUrgent
    ? 'text-red-300'
    : isCaution
      ? 'text-amber-300'
      : 'text-white'
  const ringColor = isUrgent
    ? 'stroke-red-400'
    : isCaution
      ? 'stroke-amber-300'
      : 'stroke-brand-300'
  // SVG circle circumference; stroke-dasharray + stroke-dashoffset gives us
  // a clockwise-emptying ring without re-rendering on every tick.
  const radius = 22
  const circumference = 2 * Math.PI * radius
  const dashOffset = circumference * (1 - pct / 100)
  return (
    <div className="inline-flex items-center gap-2">
      <span className="sr-only">{label}</span>
      <div className="relative h-12 w-12">
        <svg
          viewBox="0 0 50 50"
          className="h-full w-full -rotate-90"
          aria-hidden="true"
        >
          <circle
            cx="25"
            cy="25"
            r={radius}
            className="stroke-white/10"
            strokeWidth="4"
            fill="none"
          />
          <circle
            cx="25"
            cy="25"
            r={radius}
            className={`${ringColor} transition-[stroke-dashoffset] duration-1000 ease-linear`}
            strokeWidth="4"
            fill="none"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <Clock
            className={`h-3.5 w-3.5 ${isUrgent ? 'text-red-300' : 'text-brand-100/70'}`}
            aria-hidden="true"
          />
        </div>
      </div>
      <span
        className={`font-display text-2xl font-bold tabular-nums ${numeralColor}`}
        aria-live="polite"
        aria-atomic="true"
      >
        {remaining}
        <span className="ms-1 text-sm text-brand-100/70">{unit}</span>
      </span>
    </div>
  )
}

type PlayerMcqGridProps = {
  options: string[]
  disabled: boolean
  submittedAnswer: string | null
  onSelect: (value: string) => void
}

/**
 * The four canonical answer tiles. Colour mapping is deliberate (red /
 * blue / yellow / green) — players see the same colour scheme on the
 * host's projector, so the visual handshake "I picked the red one" works
 * across the room without spoken context.
 *
 * Tile sizing targets a single-thumb tap on a phone (min-h-24 + chunky
 * padding) and a comfortable hit area on desktop. After submission, the
 * non-chosen tiles dim while the chosen tile stays bright with a check
 * affordance — the player sees their own selection persist as the round
 * advances toward the host's reveal.
 */
function PlayerMcqGrid({
  options,
  disabled,
  submittedAnswer,
  onSelect,
}: PlayerMcqGridProps) {
  return (
    <ul role="list" className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
      {options.map((opt, i) => {
        const value = String(i)
        const isChosen = submittedAnswer === value
        const dim = disabled && !isChosen
        return (
          <li key={`${i}-${opt}`}>
            <button
              type="button"
              onClick={() => onSelect(value)}
              disabled={disabled}
              aria-pressed={isChosen}
              className={`relative flex min-h-24 sm:min-h-28 w-full items-center gap-4 rounded-2xl ps-5 pe-5 py-5 text-start shadow-xl transition-all duration-200 ${PLAYER_TILE_COLORS[i % PLAYER_TILE_COLORS.length]} ${
                dim ? 'opacity-30 saturate-50' : ''
              } ${
                disabled
                  ? 'cursor-default'
                  : 'hover:scale-[1.02] active:scale-[0.98] cursor-pointer'
              } ${
                isChosen
                  ? 'scale-[1.02] ring-4 ring-white/80 ring-offset-2 ring-offset-brand-900'
                  : ''
              } focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-brand-900`}
            >
              <span
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-black/25 text-white font-bold font-display text-base tabular-nums"
                aria-hidden="true"
              >
                {i + 1}
              </span>
              <span className="text-lg sm:text-xl font-bold font-display text-white leading-snug">
                {opt}
              </span>
              {isChosen ? (
                <CheckCircle2
                  className="absolute end-4 top-4 h-6 w-6 text-white drop-shadow-md"
                  aria-hidden="true"
                />
              ) : null}
            </button>
          </li>
        )
      })}
    </ul>
  )
}

// Task-specified colour palette for the four MCQ tiles. Kept fully opaque
// (no `/90` suffix) so the player's primary CTA stays as loud as possible.
const PLAYER_TILE_COLORS = [
  'bg-red-500 hover:bg-red-400',
  'bg-blue-500 hover:bg-blue-400',
  'bg-yellow-500 hover:bg-yellow-400',
  'bg-green-500 hover:bg-green-400',
] as const

type PlayerOrderGridProps = {
  items: string[]
  selection: number[]
  disabled: boolean
  onSelect: (itemIndex: number) => void
  onClear: () => void
  onSubmit: () => void
  clearLabel: string
  submitLabel: string
  slotLabel: (n: number) => string
}

/**
 * Numbered-selection UI for Order/Sequence questions. Players tap each
 * displayed item in the order they think is correct; the tile shows
 * the chosen position (1, 2, 3, …) as soon as it's picked, and the
 * "Submit" button enables once all items are placed. No drag-and-drop
 * (MVP) — tap-to-place is friendlier on phones and keeps the keyboard
 * shortcuts (1-N) consistent with the other question types.
 */
function PlayerOrderGrid({
  items,
  selection,
  disabled,
  onSelect,
  onClear,
  onSubmit,
  clearLabel,
  submitLabel,
  slotLabel,
}: PlayerOrderGridProps) {
  const total = items.length
  const allPlaced = selection.length === total && total > 0
  return (
    <div className="flex flex-col gap-4">
      <ul role="list" className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        {items.map((item, i) => {
          const placedAt = selection.indexOf(i)
          const isPlaced = placedAt !== -1
          const dim = disabled
          return (
            <li key={`${i}-${item}`}>
              <button
                type="button"
                onClick={() => {
                  if (disabled || isPlaced) return
                  onSelect(i)
                }}
                disabled={disabled || isPlaced}
                aria-pressed={isPlaced}
                aria-label={isPlaced ? slotLabel(placedAt + 1) : undefined}
                className={`relative flex min-h-20 sm:min-h-24 w-full items-center gap-4 rounded-2xl ps-5 pe-5 py-4 text-start shadow-xl transition-all duration-200 ${
                  isPlaced
                    ? 'bg-amber-500'
                    : 'bg-white/10 hover:bg-white/15 backdrop-blur-sm'
                } ${dim ? 'opacity-60 saturate-75' : ''} ${
                  disabled || isPlaced
                    ? 'cursor-default'
                    : 'hover:scale-[1.02] active:scale-[0.98] cursor-pointer'
                } focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-brand-900`}
              >
                <span
                  className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-bold font-display text-base tabular-nums ${
                    isPlaced
                      ? 'bg-white text-amber-700'
                      : 'bg-black/25 text-white'
                  }`}
                  aria-hidden="true"
                >
                  {isPlaced ? placedAt + 1 : i + 1}
                </span>
                <span className="text-base sm:text-lg font-bold font-display text-white leading-snug">
                  {item}
                </span>
              </button>
            </li>
          )
        })}
      </ul>

      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={onClear}
          disabled={disabled || selection.length === 0}
          className="inline-flex items-center justify-center rounded-xl border border-white/15 bg-white/5 ps-5 pe-5 py-3 text-sm font-semibold text-brand-100 hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
        >
          {clearLabel}
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={disabled || !allPlaced}
          className="inline-flex flex-1 items-center justify-center rounded-xl bg-amber-400 ps-5 pe-5 py-3 text-base font-bold font-display text-amber-900 shadow-xl hover:bg-amber-300 disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-amber-200/70 focus-visible:ring-offset-2 focus-visible:ring-offset-brand-900"
        >
          {submitLabel}
        </button>
      </div>
    </div>
  )
}

type PlayerTrueFalseGridProps = {
  trueLabel: string
  falseLabel: string
  disabled: boolean
  submittedAnswer: string | null
  onSelect: (value: string) => void
}

function PlayerTrueFalseGrid({
  trueLabel,
  falseLabel,
  disabled,
  submittedAnswer,
  onSelect,
}: PlayerTrueFalseGridProps) {
  const cards: Array<{
    value: 'true' | 'false'
    label: string
    tone: string
    keyHint: string
  }> = [
    {
      value: 'true',
      label: trueLabel,
      tone: 'bg-green-500 hover:bg-green-400',
      keyHint: '1',
    },
    {
      value: 'false',
      label: falseLabel,
      tone: 'bg-red-500 hover:bg-red-400',
      keyHint: '2',
    },
  ]
  return (
    <ul role="list" className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
      {cards.map((card) => {
        const isChosen = submittedAnswer === card.value
        const dim = disabled && !isChosen
        return (
          <li key={card.value}>
            <button
              type="button"
              onClick={() => onSelect(card.value)}
              disabled={disabled}
              aria-pressed={isChosen}
              className={`relative flex min-h-32 sm:min-h-36 w-full flex-col items-center justify-center gap-2 rounded-2xl ps-5 pe-5 py-5 shadow-xl transition-all duration-200 ${card.tone} ${
                dim ? 'opacity-30 saturate-50' : ''
              } ${
                disabled
                  ? 'cursor-default'
                  : 'hover:scale-[1.02] active:scale-[0.98] cursor-pointer'
              } ${
                isChosen
                  ? 'scale-[1.02] ring-4 ring-white/80 ring-offset-2 ring-offset-brand-900'
                  : ''
              } focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-brand-900`}
            >
              <span
                className="absolute start-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-full bg-black/25 text-white font-bold font-display text-sm tabular-nums"
                aria-hidden="true"
              >
                {card.keyHint}
              </span>
              <span className="font-display text-3xl sm:text-4xl font-bold text-white">
                {card.label}
              </span>
              {isChosen ? (
                <CheckCircle2
                  className="absolute end-4 top-4 h-6 w-6 text-white drop-shadow-md"
                  aria-hidden="true"
                />
              ) : null}
            </button>
          </li>
        )
      })}
    </ul>
  )
}

type SubmittedBadgeProps = {
  label: string
  hint: string
}

function SubmittedBadge({ label, hint }: SubmittedBadgeProps) {
  return (
    <div
      className="mb-5 mx-auto inline-flex items-center gap-3 rounded-full border border-emerald-300/40 bg-emerald-400/10 backdrop-blur-sm ps-5 pe-5 py-2.5 text-emerald-50"
      role="status"
      aria-live="polite"
    >
      <CheckCircle2 className="h-5 w-5 text-emerald-300" aria-hidden="true" />
      <div className="flex flex-col items-start text-start">
        <span className="text-sm font-bold font-display">{label}</span>
        <span className="text-xs text-emerald-200/80">{hint}</span>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Phase: Results (per-question feedback)
// ─────────────────────────────────────────────────────────────────────────────

type ResultsPanelProps = {
  result: { points: number; isCorrect: boolean } | null
  totalScore: number
  correctLabel: string
  wrongLabel: string
  pointsLabel: (points: number) => string
  noPointsLabel: string
  totalScoreLabel: string
  waitingLabel: string
  /** Arabic-only explanation to show below the score, or undefined to hide. */
  explanation?: string | undefined
  explanationLabel: string
  /**
   * Solo mode: hide the "waiting for others" line (nobody to wait for) and
   * surface an explicit advance button instead, since there is no host on a
   * separate device to move the game forward.
   */
  solo?: boolean | undefined
  onAdvance?: (() => void) | undefined
  advanceLabel?: string | undefined
}

function ResultsPanel({
  result,
  totalScore,
  correctLabel,
  wrongLabel,
  pointsLabel,
  noPointsLabel,
  totalScoreLabel,
  waitingLabel,
  explanation,
  explanationLabel,
  solo = false,
  onAdvance,
  advanceLabel,
}: ResultsPanelProps) {
  // `result === null` happens only if the player joined mid-question, missed
  // the submission window, and the server still emitted answersRevealed.
  // Treat it as "no points this round" — same visual weight as a wrong
  // answer but a softer copy so the player doesn't feel penalised for a
  // timing edge case.
  const isCorrect = result?.isCorrect ?? false
  const points = result?.points ?? 0
  const accent = isCorrect ? 'bg-green-500' : 'bg-red-500'
  const accentRing = isCorrect ? 'ring-green-300/40' : 'ring-red-300/40'
  const Icon = isCorrect ? CheckCircle2 : XCircle
  return (
    <section className="flex flex-1 flex-col items-center justify-center text-center">
      <div
        className={`inline-flex h-24 w-24 items-center justify-center rounded-full ${accent} ring-4 ${accentRing} shadow-2xl animate-[fadeIn_300ms_ease-out]`}
        aria-hidden="true"
      >
        <Icon className="h-12 w-12 text-white" strokeWidth={2.5} />
      </div>

      <h2 className="mt-6 font-display text-3xl sm:text-4xl font-bold tracking-tight text-white">
        {isCorrect ? correctLabel : wrongLabel}
      </h2>

      <p className="mt-3 text-base sm:text-lg text-brand-100/80">
        {points > 0 ? (
          <span className="inline-flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-amber-300" aria-hidden="true" />
            <span className="font-display text-2xl font-bold text-amber-200 tabular-nums">
              {pointsLabel(points)}
            </span>
          </span>
        ) : (
          <span>{noPointsLabel}</span>
        )}
      </p>

      <div className="mt-10 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm ps-6 pe-6 py-5">
        <div className="text-xs font-semibold uppercase tracking-wider text-brand-100/70">
          {totalScoreLabel}
        </div>
        <div className="mt-1 font-display text-5xl font-bold tabular-nums text-white">
          {totalScore}
        </div>
      </div>

      {explanation ? (
        <p className="mt-6 max-w-md text-sm leading-relaxed text-brand-100/60">
          <span className="font-semibold text-brand-100/80">{explanationLabel}:</span>{' '}
          {explanation}
        </p>
      ) : null}

      {solo ? (
        <button
          type="button"
          onClick={onAdvance}
          className="mt-10 inline-flex items-center justify-center gap-2 rounded-2xl bg-white ps-8 pe-8 py-4 text-lg font-bold font-display text-brand-900 shadow-xl transition-transform duration-150 hover:bg-brand-50 hover:scale-[1.02] active:scale-100 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-brand-900"
        >
          <ArrowLeft className="h-5 w-5" aria-hidden="true" />
          {advanceLabel ? <span>{advanceLabel}</span> : null}
        </button>
      ) : (
        <p className="mt-8 inline-flex items-center gap-2 text-sm text-brand-100/60">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          <span>{waitingLabel}</span>
        </p>
      )}
    </section>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Phase: Leaderboard interstitial
// ─────────────────────────────────────────────────────────────────────────────

type BetweenRoundsPanelProps = {
  score: number
  waitingLabel: string
  totalScoreLabel: string
}

/**
 * Brief between-questions panel surfaced while the host's projector is
 * showing the room-scale leaderboard. The player sees a compact summary
 * — their score, a waiting indicator — rather than a duplicate of the
 * full leaderboard, which would not fit on a phone anyway and would
 * compete with the projector for attention.
 */
function BetweenRoundsPanel({
  score,
  waitingLabel,
  totalScoreLabel,
}: BetweenRoundsPanelProps) {
  return (
    <section className="flex flex-1 flex-col items-center justify-center text-center">
      <Trophy className="h-12 w-12 text-amber-200" aria-hidden="true" />
      <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm ps-6 pe-6 py-5">
        <div className="text-xs font-semibold uppercase tracking-wider text-brand-100/70">
          {totalScoreLabel}
        </div>
        <div className="mt-1 font-display text-5xl font-bold tabular-nums text-white">
          {score}
        </div>
      </div>
      <p className="mt-8 inline-flex items-center gap-2 text-sm text-brand-100/60">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        <span>{waitingLabel}</span>
      </p>
    </section>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Phase: Final
// ─────────────────────────────────────────────────────────────────────────────

type FinalPanelProps = {
  nickname: string
  score: number
  leaderboard: LeaderboardEntry[]
  heading: string
  subheading: string
  totalScoreLabel: string
  rankLabel: string
  rankSuffixTemplate: (total: number) => string
  /** Solo mode: surface an explicit exit-to-dashboard button on the final screen. */
  solo?: boolean | undefined
  onExit?: (() => void) | undefined
  exitLabel?: string | undefined
}

function FinalPanel({
  nickname,
  score,
  leaderboard,
  heading,
  subheading,
  totalScoreLabel,
  rankLabel,
  rankSuffixTemplate,
  solo = false,
  onExit,
  exitLabel,
}: FinalPanelProps) {
  // Find this player's row. Server may not have provided an explicit
  // `rank` field on every entry — sort by score descending as a fallback
  // and treat the player's array index as their rank.
  const sorted = [...leaderboard].sort((a, b) => b.score - a.score)
  const myIndex = sorted.findIndex(
    (entry) => entry.nickname === nickname && entry.score === score,
  )
  const myEntry = myIndex >= 0 ? sorted[myIndex] : null
  const myRank = myEntry?.rank ?? (myIndex >= 0 ? myIndex + 1 : null)
  const total = sorted.length
  const isPodium = myRank !== null && myRank <= 3

  return (
    <section className="flex flex-1 flex-col items-center justify-center text-center">
      <div
        className={`inline-flex h-20 w-20 items-center justify-center rounded-full ${
          isPodium
            ? 'bg-amber-300/15 ring-2 ring-amber-300/50'
            : 'bg-white/10 ring-1 ring-white/15'
        }`}
        aria-hidden="true"
      >
        <Trophy
          className={`h-10 w-10 ${isPodium ? 'text-amber-200' : 'text-brand-100'}`}
        />
      </div>

      <h2 className="mt-6 font-display text-4xl sm:text-5xl font-bold tracking-tight text-white">
        {heading}
      </h2>
      <p className="mt-2 text-base sm:text-lg text-brand-100/80">{subheading}</p>

      {myRank !== null ? (
        <div className="mt-8 inline-flex flex-col items-center rounded-3xl border border-white/10 bg-white/5 backdrop-blur-sm ps-8 pe-8 py-6 shadow-2xl">
          <div className="text-xs font-semibold uppercase tracking-wider text-brand-100/70">
            {rankLabel}
          </div>
          <div className="mt-1 font-display text-7xl font-bold tabular-nums text-white">
            #{myRank}
          </div>
          {total > 0 ? (
            <div className="mt-1 text-sm text-brand-100/70">
              {rankSuffixTemplate(total)}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm ps-6 pe-6 py-4">
        <div className="text-xs font-semibold uppercase tracking-wider text-brand-100/70">
          {totalScoreLabel}
        </div>
        <div className="mt-1 font-display text-4xl font-bold tabular-nums text-amber-200">
          {score}
        </div>
      </div>

      {nickname ? (
        <p className="mt-8 text-sm text-brand-100/60">{nickname}</p>
      ) : null}

      {solo && onExit ? (
        <button
          type="button"
          onClick={onExit}
          className="mt-10 inline-flex items-center justify-center gap-2 rounded-2xl bg-white ps-8 pe-8 py-4 text-lg font-bold font-display text-brand-900 shadow-xl transition-transform duration-150 hover:bg-brand-50 hover:scale-[1.02] active:scale-100 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-brand-900"
        >
          <ArrowLeft className="h-5 w-5" aria-hidden="true" />
          {exitLabel ? <span>{exitLabel}</span> : null}
        </button>
      ) : null}
    </section>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Auxiliary panels (connecting / error)
// ─────────────────────────────────────────────────────────────────────────────

function ConnectingPanel({ message }: { message: string }) {
  return (
    <section className="flex flex-1 flex-col items-center justify-center text-center">
      <div
        className="h-12 w-12 animate-spin rounded-full border-4 border-white/20 border-t-white"
        role="status"
        aria-live="polite"
      >
        <span className="sr-only">{message}</span>
      </div>
      <p className="mt-6 text-base text-brand-100/80">{message}</p>
    </section>
  )
}

function ErrorPanel({
  message,
  retryLabel,
  onRetry,
}: {
  message: string
  retryLabel: string
  onRetry: () => void
}) {
  return (
    <section className="flex flex-1 flex-col items-center justify-center text-center">
      <div
        className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-red-500/20 ring-2 ring-red-400/40"
        aria-hidden="true"
      >
        <X className="h-8 w-8 text-red-200" strokeWidth={2.5} />
      </div>
      <p
        className="mt-6 max-w-xs text-base leading-relaxed text-red-100"
        role="alert"
      >
        {message}
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-8 inline-flex items-center justify-center gap-2 rounded-2xl bg-white ps-6 pe-6 py-3 text-base font-bold text-brand-900 shadow-xl transition-transform duration-150 hover:bg-brand-50 hover:scale-[1.02] active:scale-100 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-brand-900"
      >
        <RotateCcw className="h-5 w-5" aria-hidden="true" />
        <span>{retryLabel}</span>
      </button>
    </section>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Hooks + helpers (duplicated from host panel; consolidation TBD)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * 1-Hz countdown that resets every time the question index changes. Drifts
 * by ≤1s from the server-authoritative timer — acceptable for a 5–120s
 * window since the server is the authority for round transitions.
 *
 * (Duplicates the helper in `_app/games/host/$gameId.tsx`. Extracting to a
 * shared `useCountdown` hook is deferred until a third caller emerges.)
 */
function useCountdown(questionIndex: number, totalSeconds: number): number {
  const [remaining, setRemaining] = useState(totalSeconds)
  useEffect(() => {
    setRemaining(totalSeconds)
    if (totalSeconds <= 0) {
      return
    }
    const interval = window.setInterval(() => {
      setRemaining((prev) => (prev > 0 ? prev - 1 : 0))
    }, 1000)
    return () => {
      window.clearInterval(interval)
    }
  }, [questionIndex, totalSeconds])
  return remaining
}

/**
 * Render a quiz question prompt with inline / display KaTeX math
 * substitutions. Plain text is HTML-escaped; KaTeX is invoked with
 * `trust:false` and `strict:true` so unsafe commands and undefined
 * symbols are refused — KaTeX throws and we surface a localised badge.
 *
 * (Duplicated from `_app/games/host/$gameId.tsx` and `QuizEditor.tsx`;
 * three copies now exist and a shared util is overdue but out of scope
 * for T42.)
 */
function renderMathPreview(text: string): string {
  const segments: string[] = []
  const pattern = /\$\$([\s\S]+?)\$\$|\$([^$\n]+?)\$/g
  let lastIndex = 0
  let match: RegExpExecArray | null = pattern.exec(text)
  while (match !== null) {
    if (match.index > lastIndex) {
      segments.push(escapeHtml(text.slice(lastIndex, match.index)))
    }
    const displayMath = match[1]
    const inlineMath = match[2]
    if (typeof displayMath === 'string') {
      segments.push(renderMathSafe(displayMath, true))
    } else if (typeof inlineMath === 'string') {
      segments.push(renderMathSafe(inlineMath, false))
    }
    lastIndex = pattern.lastIndex
    match = pattern.exec(text)
  }
  if (lastIndex < text.length) {
    segments.push(escapeHtml(text.slice(lastIndex)))
  }
  return segments.join('').replace(/\n/g, '<br>')
}

const KATEX_MACROS = {
  '\\RR': '\\mathbb{R}',
  '\\NN': '\\mathbb{N}',
  '\\ZZ': '\\mathbb{Z}',
  '\\QQ': '\\mathbb{Q}',
  '\\CC': '\\mathbb{C}',
} as const

function renderMathSafe(math: string, displayMode: boolean): string {
  try {
    return katex.renderToString(math, {
      displayMode,
      macros: KATEX_MACROS,
      trust: false,
      strict: true,
      throwOnError: true,
    })
  } catch {
    return '<span class="text-red-300 text-base">[خطأ في الصيغة]</span>'
  }
}

function escapeHtml(input: string): string {
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}
